// Save state indicator — plan §6.1 durum matrisi + UX P1 K4 "invalid".
// Kopya tek kaynak: lib/form-save-copy.ts. "Yeniden deneniyor…" yalnız gerçekten bir
// yeniden deneme zamanlandığında (saveState "retry") görünür; doğrulama hatası "invalid".

import { useFormFillStore, type SaveState } from "../../stores/form-fill-store";
import { selectHeldInvalidCodes } from "../../stores/form-fill-flush";
import { FORM_SAVE_COPY as C } from "../../lib/form-save-copy";
import { goToFirstInvalid } from "./MissingPanel";

interface Props {
  state: SaveState;
  lastSavedAt: string | null;
  pendingCount: number;
  /** Sunucunun reddettiği ve düzeltilmesi beklenen alan sayısı. */
  invalidCount?: number;
  /** R1 — sunucunun daha yeni değerini yüklediği (yazımımızı atladığı) alan sayısı. */
  noticeCount?: number;
  /** Verilirse kaydedilemeyen/bildirimli alan varken gösterge ilk alana götüren düğme olur. */
  onGoToInvalid?: () => void;
}

const COLOR: Record<SaveState, string | null> = {
  idle: null,
  typing: "var(--color-text-3)",
  saving: "var(--color-warning)",
  saved: "var(--color-success)",
  retry: "var(--color-warning)",
  offline: "var(--color-error)",
  syncing: "var(--color-primary)",
  conflict: "var(--color-error)",
  invalid: "var(--color-error)",
  failed: "var(--color-error)",
};

function labelFor(state: SaveState, invalidCount: number): string | null {
  if (state === "idle") return null;
  // İnceleme M2 — sayaç 0 iken "0 alan kaydedilemedi" yazılmaz (savunma).
  if (state === "invalid") return invalidCount > 0 ? C.invalid(invalidCount) : null;
  return C.indicator[state];
}

export function SaveIndicator({
  state,
  lastSavedAt,
  pendingCount,
  invalidCount = 0,
  noticeCount = 0,
  onGoToInvalid,
}: Props): JSX.Element | null {
  // idle/saved iken de reddedilmiş alan kalmışsa gösterge kaybolmaz.
  const quiet = state === "idle" || state === "saved";
  const effective: SaveState = invalidCount > 0 && quiet ? "invalid" : state;
  // R1 — atlanan yazım varken "Kaydedildi ✓" denmez; bildirim etiketi gösterilir.
  const showNotice = noticeCount > 0 && quiet && effective !== "invalid";
  const label = showNotice ? C.supersededIndicator(noticeCount) : labelFor(effective, invalidCount);
  const color = showNotice ? "var(--color-warning)" : COLOR[effective];
  if (!label || !color) return null;
  const showSuffix = invalidCount > 0 && effective !== "invalid";
  const clickable = (invalidCount > 0 || noticeCount > 0) && onGoToInvalid !== undefined;

  const body = (
    <>
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: color,
          display: "inline-block",
        }}
      />
      <span
        style={{ color: effective === "invalid" ? "var(--color-error)" : "var(--color-text-2)" }}
      >
        {label}
      </span>
      {showSuffix && (
        <span style={{ color: "var(--color-error)" }}>{C.invalidSuffix(invalidCount)}</span>
      )}
      {effective === "offline" && pendingCount > 0 && (
        <span style={{ color: "var(--color-text-3)", fontFamily: "var(--font-mono)" }}>
          {C.offlineQueued(pendingCount)}
        </span>
      )}
      {effective === "saved" && !showNotice && lastSavedAt && (
        <span style={{ color: "var(--color-text-3)", fontFamily: "var(--font-mono)" }}>
          {formatTime(lastSavedAt)}
        </span>
      )}
    </>
  );

  const style = {
    background: "var(--color-surface)",
    border: `1px solid ${invalidCount > 0 ? "color-mix(in srgb, var(--color-error) 35%, white)" : "var(--color-border)"}`,
    fontFamily: "var(--font-sans)",
  };

  // İnceleme M9 — düğme hâlinde de canlı bölge korunur (ekran okuyucu durum değişimini duyurur).
  if (clickable) {
    return (
      <span role="status" data-testid="save-indicator" data-save-state={effective}>
        <button
          type="button"
          onClick={onGoToInvalid}
          title={C.invalidGoTo}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
          style={{ ...style, cursor: "pointer" }}
        >
          {body}
        </button>
      </span>
    );
  }
  return (
    <div
      data-testid="save-indicator"
      data-save-state={effective}
      role="status"
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
      style={style}
    >
      {body}
    </div>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

/** FormFill üst barı için store'a bağlı kap — FormFill.tsx'i büyütmemek için burada. */
export function FormSaveIndicator(): JSX.Element | null {
  const state = useFormFillStore((s) => s.saveState);
  const lastSavedAt = useFormFillStore((s) => s.lastSavedAt);
  const pendingCount = useFormFillStore((s) => Object.keys(s.pending).length);
  const invalidCount = useFormFillStore((s) => selectHeldInvalidCodes(s).length);
  const noticeCount = useFormFillStore((s) => Object.keys(s.supersededByCode).length);
  return (
    <SaveIndicator
      state={state}
      lastSavedAt={lastSavedAt}
      pendingCount={pendingCount}
      invalidCount={invalidCount}
      noticeCount={noticeCount}
      onGoToInvalid={goToFirstInvalid}
    />
  );
}
